import { DSLQueryClauseDTO } from '@/shared/api/opensearch/bool-query';
import { z } from 'zod/v4';

export const DSLQueryDTO = DSLQueryClauseDTO;

export type DSLQuery = z.infer<typeof DSLQueryDTO>;

type FieldName<T> = keyof T & string;
type SortableField<T> = FieldName<T> | `${FieldName<T>}.keyword`;
type NumericField<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T] &
  string;
type DateField<T> = {
  [K in keyof T]: T[K] extends Date | string ? K : never;
}[keyof T] &
  string;
type AnyField<T> = FieldName<T> | (string & {});

export type DSLAggregation<TSource = Record<string, unknown>> =
  | {
      terms: {
        field: SortableField<TSource> | AnyField<TSource>;
        size?: number;
      };
      aggs?: DSLAggregations<TSource>;
    }
  | { avg: { field: NumericField<TSource> | AnyField<TSource> } }
  | { sum: { field: NumericField<TSource> | AnyField<TSource> } }
  | { min: { field: NumericField<TSource> | AnyField<TSource> } }
  | { max: { field: NumericField<TSource> | AnyField<TSource> } }
  | { value_count: { field: AnyField<TSource> } }
  | { cardinality: { field: AnyField<TSource> } }
  | {
      date_histogram: {
        field: DateField<TSource> | AnyField<TSource>;
        calendar_interval?: string;
        fixed_interval?: string;
      };
      aggs?: DSLAggregations<TSource>;
    }
  | { filter: DSLQuery; aggs?: DSLAggregations<TSource> }
  | { nested: { path: AnyField<TSource> }; aggs?: DSLAggregations<TSource> }
  | {
      top_hits: {
        size?: number;
        _source?: DSLSourceFilter<TSource>;
        sort?: DSLSort<TSource>;
      };
    };

export type DSLAggregations<TSource = Record<string, unknown>> = Record<
  string,
  DSLAggregation<TSource>
>;
const DSLSortDTO = z.array(
  z.union([
    z.string(),
    z.record(
      z.string(),
      z.object({
        order: z.enum(['asc', 'desc']).optional(),
      }),
    ),
  ]),
);

export const DSLAggregationDTO: z.ZodType<DSLAggregation> = z.lazy(() =>
  z.union([
    z.object({
      terms: z.object({ field: z.string(), size: z.number().optional() }),
      aggs: z.record(z.string(), DSLAggregationDTO).optional(),
    }),
    z.object({ avg: z.object({ field: z.string() }) }),
    z.object({ sum: z.object({ field: z.string() }) }),
    z.object({ min: z.object({ field: z.string() }) }),
    z.object({ max: z.object({ field: z.string() }) }),
    z.object({ value_count: z.object({ field: z.string() }) }),
    z.object({ cardinality: z.object({ field: z.string() }) }),
    z.object({
      date_histogram: z.object({
        field: z.string(),
        calendar_interval: z.string().optional(),
        fixed_interval: z.string().optional(),
      }),
      aggs: z.record(z.string(), DSLAggregationDTO).optional(),
    }),
    z.object({
      filter: z.unknown(),
      aggs: z.record(z.string(), DSLAggregationDTO).optional(),
    }),
    z.object({
      nested: z.object({ path: z.string() }),
      aggs: z.record(z.string(), DSLAggregationDTO).optional(),
    }),
    z.object({
      top_hits: z.object({
        size: z.number().optional(),
        _source: z.unknown().optional(),
        sort: DSLSortDTO.optional(),
      }),
    }),
  ]),
);

type DSLAggBucket<TSource = Record<string, unknown>> = {
  key: string | number;
  doc_count: number;
  [nestedAgg: string]: unknown;
} & Partial<{ _source: TSource }>;

export type DSLAggregationResult<TSource = Record<string, unknown>> =
  | {
      buckets: DSLAggBucket<TSource>[];
      doc_count_error_upper_bound?: number;
      sum_other_doc_count?: number;
    }
  | { value: number | null }
  | { doc_count: number; [nestedAgg: string]: unknown };

export type DSLAggregationResults<
  TSource = Record<string, unknown>,
  TAggs extends DSLAggregations<TSource> = DSLAggregations<TSource>,
> = {
  [K in keyof TAggs]: DSLAggregationResult<TSource>;
};

const AggregationBucketDTO = z.looseObject({
  key: z.union([z.string(), z.number()]),
  doc_count: z.number(),
});

const AggregationResultDTO = z.looseObject({
  buckets: z.array(AggregationBucketDTO).optional(),
  value: z.unknown().optional(),
  doc_count: z.number().optional(),
  doc_count_error_upper_bound: z.number().optional(),
  sum_other_doc_count: z.number().optional(),
});

export type DSLSourceFilter<TSource = Record<string, unknown>> =
  | boolean
  | AnyField<TSource>[]
  | { includes?: AnyField<TSource>[]; excludes?: AnyField<TSource>[] };

export type DSLSort<TSource = Record<string, unknown>> = Array<
  AnyField<TSource> | Record<AnyField<TSource>, { order?: 'asc' | 'desc' }>
>;

export type DSLSearchRequest<
  TSource = Record<string, unknown>,
  TAggs extends DSLAggregations<TSource> = DSLAggregations<TSource>,
> = {
  index?: string;
  from?: number;
  size?: number;
  query?: DSLQuery;
  aggs?: TAggs;
  track_total_hits?: boolean;
  collapse?: { field: AnyField<TSource> };
  sort?: DSLSort<TSource>;
  _source?: DSLSourceFilter<TSource>;
};

export const DSLSearchRequestDTO = () =>
  z.object({
    index: z.string().optional(),
    from: z.number().optional(),
    size: z.number().optional(),
    query: DSLQueryDTO.optional(),
    aggs: z.record(z.string(), DSLAggregationDTO).optional(),
    track_total_hits: z.boolean().optional(),
    collapse: z.object({ field: z.string() }).optional(),
    sort: z
      .array(
        z.union([
          z.string(),
          z.record(
            z.string(),
            z.looseObject({ order: z.enum(['asc', 'desc']).optional() }),
          ),
        ]),
      )
      .optional(),
    _source: z
      .union([
        z.boolean(),
        z.array(z.string()),
        z.object({
          includes: z.array(z.string()).optional(),
          excludes: z.array(z.string()).optional(),
        }),
      ])
      .optional(),
  });

export const DSLHitDTO = (sourceSchema?: z.ZodTypeAny) =>
  z.looseObject({
    _index: z.string(),
    _id: z.string(),
    _score: z.number().nullish(),
    _source: sourceSchema ?? z.unknown(),
  });

export type DSLHit<TSource = unknown> = Omit<
  z.infer<ReturnType<typeof DSLHitDTO>>,
  '_source'
> & {
  _source: TSource;
};

export const DSLResponseDTO = (sourceSchema?: z.ZodTypeAny) =>
  z.object({
    cursor: z.string().nullish(),
    pit_id: z.string().nullish(),
    took: z.number(),
    timed_out: z.boolean(),
    _shards: z.object({
      total: z.number(),
      successful: z.number(),
      skipped: z.number(),
      failed: z.number(),
    }),
    hits: z.object({
      total: z.object({ value: z.number(), relation: z.string() }).nullish(),
      max_score: z.number().nullish(),
      hits: z.array(DSLHitDTO(sourceSchema)).nullish(),
    }),
    aggregations: z.record(z.string(), AggregationResultDTO).optional(),
  });

type DSLResponseBase = z.infer<ReturnType<typeof DSLResponseDTO>>;

export type DSLResponse<
  TSource = Record<string, unknown>,
  TAggs extends DSLAggregations<TSource> = DSLAggregations<TSource>,
> = Omit<DSLResponseBase, 'hits' | 'aggregations'> & {
  hits: Omit<DSLResponseBase['hits'], 'hits'> & {
    hits?: DSLHit<TSource>[] | null;
  };
  aggregations?: DSLAggregationResults<TSource, TAggs>;
};
export const DSLFieldTypesDTO = z.enum([
  'text',
  'keyword',
  'search_as_you_type',
  'prefix',
  'integer',
  'float',
  'short',
  'long',
  'half_float',
  'scaled_float',
  'boolean',
  'date',
  'ip',
  'geo_point',
  'geo_shape',
  'nested',
  'object',
  'alias',
]);
export const DSLFieldCapsDTO = z.object({
  indices: z.array(z.string()),
  fields: z.record(
    z.string(),
    z.looseRecord(
      z.string(),
      z.object({
        type: z.string(),
        searchable: z.boolean(),
        aggregatable: z.boolean(),
      }),
    ),
  ),
});

export type DSLFieldCaps = z.infer<typeof DSLFieldCapsDTO>;

export * from './bool-query';
export * from './exists-query';
export * from './match-all-query';
export * from './match-query';
export * from './multi-match-query';
export * from './prefix-query';
export * from './range-query';
export * from './term-query';
export * from './terms-query';